// Locale primitives shared by the web app and the landing page.
//
// Zod-free and browser-safe on purpose: the landing kit's runtime imports only this
// subpath. Edge functions cannot import this package (Deno, relative imports
// only), so supabase/functions/_shared/locale.ts carries a copy of the
// constants — tests/unit/locale.test.ts fails if the two drift.

export const LOCALES = ['en', 'pt'] as const
export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'en'

/** BCP 47 tag handed to Intl.* for dates, numbers and money. */
export const INTL_LOCALE: Record<Locale, string> = { en: 'en-GB', pt: 'pt-PT' }

/** How the language names itself, for switchers. */
export const LOCALE_LABEL: Record<Locale, string> = { en: 'English', pt: 'Português' }

/** Human language name handed to the model when an org has not set one. */
export const LOCALE_LANGUAGE_NAME: Record<Locale, string> = {
  en: 'English',
  pt: 'Português (Portugal)',
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

/** 'pt-BR' → 'pt', 'en-US' → 'en', anything unknown → null. */
export function normalizeLocale(tag: string | null | undefined): Locale | null {
  if (!tag) return null
  const base = tag.trim().toLowerCase().split(/[-_]/)[0]
  return isLocale(base) ? base : null
}

/** First candidate that maps to a supported locale, else the default. */
export function pickLocale(candidates: Iterable<string | null | undefined>): Locale {
  for (const candidate of candidates) {
    const locale = normalizeLocale(candidate)
    if (locale) return locale
  }
  return DEFAULT_LOCALE
}

/**
 * Fill `{name}` placeholders. Every occurrence is replaced (String.replace
 * only touches the first), and an unknown placeholder is left visible rather
 * than silently dropped, so a missing variable shows up in review.
 */
export function fmt(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? String(vars[key]) : match,
  )
}
